import { ErrorMessage } from "src/model/error-message";
import { useAlertDialog as useAlertDialogPDS } from "@preownedfront/lib-reowds";
import { useAlertDialog as useAlertDialogZDS } from "@zarafront/lib-zds";
import { i18n } from "i18next";
import React, { useContext, useCallback } from "react";
import { useTranslation } from "react-i18next";

export interface ErrorNotification {
  error: ErrorMessage;
  acceptButtonText?: string;
  cancelButtonText?: string;
  showCancel?: boolean;
  onAcceptClick?: () => void;
  onCancelClick?: () => void;
}

export type ErrorNotifier = (errorInfo: ErrorNotification, oldAlertDialog?: boolean) => void;

interface ShowErrorsContextType {
  showError: ErrorNotifier;
}

const getPropertyValue = (key: string, prefix: string, i18n: i18n): string => {
  if (!key) return "";
  
  const keys = [key, `${prefix}${key}`];
  const foundKey = keys.find((k) => i18n.exists(k));
  return foundKey ? i18n.t(foundKey) : key;
};

const ShowErrorsContext = React.createContext<ShowErrorsContextType | null>(null);

export const useShowError = (): ErrorNotifier => {
  const context = useContext(ShowErrorsContext);
  
  if (!context) {
    throw new Error("useShowError must be used within a ShowErrorsProvider");
  }
  
  return context.showError;
};

interface ShowErrorsProviderProps {
  children: React.ReactNode;
}

export const ShowErrorsProvider: React.FC<ShowErrorsProviderProps> = ({ children }) => {
  const { t, i18n } = useTranslation();
  const { alert: alertPDS } = useAlertDialogPDS();
  const { alert: alertZDS } = useAlertDialogZDS();

  const showError = useCallback((errorInfo: ErrorNotification, oldAlertDialog: boolean = true) => {
    const { error, acceptButtonText, cancelButtonText, showCancel, onAcceptClick, onCancelClick } = errorInfo;
    const { titleKey, messageKey } = error;

    const title = titleKey ? getPropertyValue(titleKey, "errors.title.", i18n) : "";
    const message = getPropertyValue(messageKey, "errors.", i18n);

    const cancelLabel = cancelButtonText ? cancelButtonText : showCancel ? t("common_dialog_cancel") : undefined;

    const dialog = {
        title,
        description: message,
        acceptLabel: acceptButtonText ?? t('common_dialog_accept'),
        cancelLabel,
        onAccept: onAcceptClick,
        onCancel: onCancelClick,
      };

    if (oldAlertDialog) {
      alertPDS(dialog);
    } else alertZDS(dialog);
    
  }, [t, i18n, alertPDS, alertZDS]);

  const contextValue = React.useMemo(() => ({
    showError
  }), [showError]);

  return (
    <ShowErrorsContext.Provider value={contextValue}>
      {children}
    </ShowErrorsContext.Provider>
  );
};

export default ShowErrorsProvider;